import type { FeatureFlagData } from '@sb/feathers-types/featureFlags';
import { FeatureFlagDataZod } from '@sb/feathers-types/featureFlags';
import * as log from '@sb/log';

import { readJSONFileWithFallback } from './util';

const ns = log.namespace('featureFlags');

const path = '/etc/standardbots/configuration/feature_flags.json';
let cache: FeatureFlagData | undefined;

export const updateFeatureFlagsCache = async (): Promise<void> => {
  cache = await readJSONFileWithFallback<FeatureFlagData>(
    path,
    FeatureFlagDataZod.parse({}),
  );
};

export const loadFeatureFlags = async (
  refreshCache = false,
): Promise<FeatureFlagData> => {
  if (!cache || refreshCache) {
    await updateFeatureFlagsCache();
  }

  // Since updateFeatureFlagsCache always sets cache, it's safe to assert it's non-undefined
  return cache as FeatureFlagData;
};

export const getFeatureFlag = async <T extends keyof FeatureFlagData>(
  flag: T,
  refreshCache = false,
): Promise<FeatureFlagData[T]> => {
  const flags = await loadFeatureFlags(refreshCache);

  return flags[flag];
};

/**
 * Synchronous version of getFeatureFlag.  Feature flags must be loaded before
 * @param flag
 * @returns
 */
export const getFeatureFlagSync = <T extends keyof FeatureFlagData>(
  flag: T,
  defaultValue: FeatureFlagData[T],
): FeatureFlagData[T] => {
  if (!cache) {
    log.warn(ns`getFeatureFlagSync`, 'Feature flags not loaded yet');

    // this default value is only relevant if there is no cache, otherwise
    // the cache itself knows the true default value
    return defaultValue;
  }

  // if flag is not in cache, return defaultValue
  if (cache[flag] === undefined) {
    return defaultValue;
  }

  return cache[flag];
};
